import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { IconeBaixo } from "../icones";

/**
 * Lista de escolha com busca (spec 059, bloco 2; Vitor, 23/10: "todos os campos de select, tenham pesquisa na
 * listagem"). O `CampoSelecao` e a `SelecaoDaPlanilha` viram esta lista a partir de `BUSCA_A_PARTIR_DE` opcoes; com
 * menos, o select nativo segue (no celular abre o seletor do sistema, e busca em tres opcoes so atrapalha).
 *
 * Padrao combobox do ARIA: o botao mostra a escolha; abrir poe o foco na busca, as setas andam pelas opcoes que batem,
 * Enter escolhe, Esc fecha e devolve o foco ao botao. A busca ignora acento e maiuscula e tambem casa com o grupo.
 * O painel abre na camada de cima do navegador (popover): a planilha que rola de lado nao corta, e dentro de um
 * dialogo ele fica por cima.
 */
export const BUSCA_A_PARTIR_DE = 8;

export type OpcaoDaLista<T extends string> = { valor: T; rotulo: string; grupo?: string };

const normal = (s: string) => s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().trim();
const ALTURA_DO_PAINEL = 320;

export function ListaComBusca<T extends string>({ id, rotulo, rotuloOculto, valor, aoMudar, opcoes, vazio, obrigatorio, disabled, invalido, descritoPor, classe, mudou }: {
  id: string;
  /** O nome do campo: rotulo visivel no `CampoSelecao` (o `label` aponta pro botao). */
  rotulo: string;
  /** Planilha: sem rotulo na tela, o nome vai no proprio botao. */
  rotuloOculto?: boolean;
  valor: T | "";
  aoMudar: (v: T) => void;
  opcoes: OpcaoDaLista<T>[];
  /** O texto da escolha vazia, quando ela existe ("Todos os planos", "Escolha o plano"). */
  vazio?: string;
  /** Como o `required` do select: a escolha vazia e so o texto do botao, nao se escolhe. */
  obrigatorio?: boolean;
  disabled?: boolean;
  invalido?: boolean;
  descritoPor?: string;
  /** Classe do botao: a do campo (`mtc-campo__entrada mtc-campo__entrada--selecao`) ou a da planilha. */
  classe: string;
  /** Planilha: a celula mudou e ainda nao foi salva. */
  mudou?: boolean;
}) {
  const [aberta, setAberta] = useState(false);
  const [termo, setTermo] = useState("");
  const [ativa, setAtiva] = useState(0);
  const [lugar, setLugar] = useState<CSSProperties>({});
  const botao = useRef<HTMLButtonElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const busca = useRef<HTMLInputElement>(null);
  const idLista = `${id}-lista`;

  const todas: OpcaoDaLista<T | "">[] = vazio !== undefined && !obrigatorio ? [{ valor: "", rotulo: vazio }, ...opcoes] : opcoes;
  const t = normal(termo);
  const batem = t ? todas.filter((o) => normal(o.rotulo).includes(t) || (o.grupo !== undefined && normal(o.grupo).includes(t))) : todas;
  // Os grupos na ordem em que aparecem, e as opcoes juntas por grupo: as setas andam na mesma ordem da tela.
  const grupos = [...new Set(batem.map((o) => o.grupo ?? ""))];
  const visiveis = grupos.flatMap((g) => batem.filter((o) => (o.grupo ?? "") === g));
  const atual = todas.find((o) => o.valor === valor);

  /** Debaixo do botao; sem espaco embaixo e com mais em cima, por cima dele. Largura do botao, nunca menos de 260. */
  const posicionar = () => {
    const r = botao.current?.getBoundingClientRect();
    if (!r) return;
    const largura = Math.min(Math.max(r.width, 260), window.innerWidth - 16);
    const esquerda = Math.min(Math.max(8, r.left), window.innerWidth - largura - 8);
    const embaixo = window.innerHeight - r.bottom;
    const emCima = embaixo < ALTURA_DO_PAINEL + 12 && r.top > embaixo;
    setLugar(emCima ? { left: esquerda, width: largura, bottom: window.innerHeight - r.top + 4, maxHeight: Math.min(ALTURA_DO_PAINEL, r.top - 12) } : { left: esquerda, width: largura, top: r.bottom + 4, maxHeight: Math.min(ALTURA_DO_PAINEL, embaixo - 12) });
  };

  const abrir = (primeiraLetra = "") => {
    if (disabled) return;
    setTermo(primeiraLetra);
    const i = todas.findIndex((o) => o.valor === valor);
    setAtiva(primeiraLetra ? 0 : Math.max(0, i));
    posicionar();
    setAberta(true);
  };
  const fechar = (devolverFoco = true) => {
    setAberta(false);
    if (devolverFoco) botao.current?.focus({ preventScroll: true });
  };
  const escolher = (o: OpcaoDaLista<T | ""> | undefined) => {
    if (!o) return;
    if (o.valor !== valor) aoMudar(o.valor as T);
    fechar();
  };

  // Camada de cima (popover manual): mostra, poe o foco na busca e leva a opcao escolhida pra vista.
  useLayoutEffect(() => {
    const p = painel.current;
    if (!aberta || !p) return;
    // Sem popover no navegador (ou nos testes de unidade), o painel fica no lugar dele, com a mesma posicao fixa.
    const temPopover = typeof p.showPopover === "function";
    if (temPopover && !p.matches(":popover-open")) p.showPopover();
    busca.current?.focus({ preventScroll: true });
    p.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
    return () => { if (temPopover && p.matches(":popover-open")) p.hidePopover(); };
  }, [aberta]);

  // Aberta: toque fora fecha; rolar ou redimensionar acompanha o botao.
  useEffect(() => {
    if (!aberta) return;
    const fora = (e: PointerEvent) => {
      const alvo = e.target as Node;
      if (!painel.current?.contains(alvo) && !botao.current?.contains(alvo)) fechar(false);
    };
    const mover = (e: Event) => { if (!painel.current?.contains(e.target as Node)) posicionar(); };
    document.addEventListener("pointerdown", fora, true);
    window.addEventListener("scroll", mover, true);
    window.addEventListener("resize", posicionar);
    return () => {
      document.removeEventListener("pointerdown", fora, true);
      window.removeEventListener("scroll", mover, true);
      window.removeEventListener("resize", posicionar);
    };
  }, [aberta]);

  // A opcao ativa sempre a vista quando as setas andam.
  useEffect(() => {
    if (aberta) document.getElementById(`${id}-op-${ativa}`)?.scrollIntoView({ block: "nearest" });
  }, [ativa, aberta, id]);

  const teclaNaBusca = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setAtiva((a) => Math.min(a + 1, visiveis.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setAtiva((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Home") { e.preventDefault(); setAtiva(0); }
    else if (e.key === "End") { e.preventDefault(); setAtiva(Math.max(0, visiveis.length - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); escolher(visiveis[ativa]); }
    // Esc fecha a lista e nao o dialogo em volta.
    else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); fechar(); }
    else if (e.key === "Tab") fechar(false);
  };
  const teclaNoBotao = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); abrir(); }
    // Comecar a digitar com a lista fechada ja abre buscando.
    else if (e.key.length === 1 && e.key !== " " && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); abrir(e.key); }
  };

  const comIndice = visiveis.map((o, i) => ({ o, i }));
  const opcao = ({ o, i }: { o: OpcaoDaLista<T | "">; i: number }) => (
    <div
      key={`${o.valor}-${i}`}
      id={`${id}-op-${i}`}
      role="option"
      aria-selected={o.valor === valor}
      data-valor={o.valor}
      data-ativa={i === ativa || undefined}
      className="mtc-lista-com-busca__opcao"
      // Sem tirar o foco da busca: o clique escolhe, a busca nao perde o que foi digitado antes disso.
      onMouseDown={(e) => e.preventDefault()}
      onMouseMove={() => setAtiva(i)}
      onClick={() => escolher(o)}
    >
      {o.rotulo}
    </div>
  );

  return (
    <div className="mtc-lista-com-busca">
      <button
        ref={botao}
        type="button"
        id={id}
        className={`${classe} mtc-lista-com-busca__botao`}
        aria-haspopup="listbox"
        aria-expanded={aberta}
        aria-controls={idLista}
        aria-label={rotuloOculto ? rotulo : undefined}
        aria-invalid={invalido || undefined}
        // O nome e o rotulo do campo; a escolha de agora vai na descricao, pro leitor de tela dizer as duas.
        aria-describedby={[`${id}-escolha`, descritoPor].filter(Boolean).join(" ")}
        data-mudou={mudou || undefined}
        // O valor escolhido, pra quem le o campo de fora (os e2e, como o `value` do select).
        data-valor={valor}
        data-vazio={atual === undefined || atual.valor === "" || undefined}
        disabled={disabled}
        onClick={() => (aberta ? fechar() : abrir())}
        onKeyDown={teclaNoBotao}
      >
        <span id={`${id}-escolha`} className="mtc-lista-com-busca__escolha">{atual?.rotulo ?? vazio ?? "Escolha"}</span>
      </button>
      <IconeBaixo className="mtc-campo__seta" tamanho={16} />
      {aberta && (
        <div ref={painel} popover="manual" className="mtc-lista-com-busca__painel" style={lugar}>
          <input
            ref={busca}
            type="search"
            className="mtc-lista-com-busca__busca"
            role="combobox"
            aria-label={`Buscar em ${rotulo}`}
            aria-expanded="true"
            aria-controls={idLista}
            aria-autocomplete="list"
            aria-activedescendant={visiveis.length ? `${id}-op-${ativa}` : undefined}
            placeholder="Buscar"
            autoComplete="off"
            value={termo}
            onChange={(e) => { setTermo(e.target.value); setAtiva(0); }}
            onKeyDown={teclaNaBusca}
          />
          <div id={idLista} role="listbox" aria-label={rotulo} className="mtc-lista-com-busca__lista">
            {!visiveis.length ? (
              <div className="mtc-lista-com-busca__nada">Nada com “{termo.trim()}”.</div>
            ) : grupos.length > 1 || grupos[0] ? (
              grupos.map((g) => (
                <div key={g} role="group" aria-label={g || undefined}>
                  {g && <div className="mtc-lista-com-busca__grupo" aria-hidden="true">{g}</div>}
                  {comIndice.filter(({ o }) => (o.grupo ?? "") === g).map(opcao)}
                </div>
              ))
            ) : (
              comIndice.map(opcao)
            )}
          </div>
        </div>
      )}
    </div>
  );
}
